import { useEffect, useCallback, useRef } from 'react';
import { X } from 'react-feather';
import { useNavigate } from 'react-router-dom';
import VoiceCardOverlay from './F8/VoiceCardOverlay';
import { useVoiceChat } from '../hooks/useVoiceChat';
import '../styles/VoiceMode.css';

const WAVEFORM_DELAYS = [0.5, 0.8, 1.1, 1.3, 0.9, 1.4, 0.7, 1.0, 0.6];

function fmtTime(s) {
  const mm = String(Math.floor(s / 60)).padStart(2, '0');
  const ss = String(s % 60).padStart(2, '0');
  return `${mm}:${ss}`;
}

/**
 * `threadId` is the f8 chat thread the caller is showing. It is handed to the
 * voice session so the transcript — and every skill card, a draft above all —
 * is persisted onto THAT thread instead of a fresh "Voice Chat" thread the user
 * would never find (PPL-534).
 */
export default function WebVoiceMode({ open, onClose, chatHistory = [], threadId = null }) {
  const navigate = useNavigate();
  const {
    state,
    phase,
    muted,
    elapsed,
    lastError,
    remainingSeconds,
    tier,
    userTranscript,
    agentTranscript,
    activeCards,
    timeWarningSeconds,
    quotaExhausted,
    quotaReason,
    start,
    stop,
    toggleMute,
    dismissCard,
    sendChatHistory,
  } = useVoiceChat();

  // Auto-connect once when opened; disconnect when closed.
  // Tracking attempts via ref keeps an error from triggering an infinite
  // reconnect loop (state idle → connecting → idle on failure → re-fire).
  const attemptedRef = useRef(false);
  // Read through a ref so a thread id arriving after mount does not re-fire the
  // connect effect (which is keyed on `open` alone, on purpose).
  const threadIdRef = useRef(threadId);
  threadIdRef.current = threadId;
  useEffect(() => {
    if (open) {
      if (!attemptedRef.current) {
        attemptedRef.current = true;
        start({ threadId: threadIdRef.current });
      }
    } else {
      attemptedRef.current = false;
      stop();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Once connected, push recent f8 chat history as runtime context.
  // Gate on `connected` so the context doc-stuffing happens after handshake.
  useEffect(() => {
    if (state !== 'connected') return;
    if (!Array.isArray(chatHistory) || chatHistory.length === 0) return;
    const normalized = chatHistory
      .filter((m) => m && typeof m.text === 'string' && m.text.length > 0)
      .slice(-12)
      .map((m) => ({
        role: m.sender === 'user' ? 'user' : 'agent',
        text: m.text,
      }));
    if (normalized.length > 0) sendChatHistory(normalized);
  }, [state, chatHistory, sendChatHistory]);

  const handleClose = useCallback(() => {
    stop();
    onClose();
  }, [stop, onClose]);

  const handleUpgrade = useCallback(() => {
    stop();
    onClose();
    navigate('/premium');
  }, [stop, onClose, navigate]);

  const handleKey = useCallback((e) => {
    if (e.key === 'Escape') handleClose();
  }, [handleClose]);

  useEffect(() => {
    if (!open) return;
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [open, handleKey]);

  if (!open) return null;

  const isConnecting = state === 'connecting';
  const visualPhase = isConnecting ? 'thinking' : phase;
  const skillActive = Array.isArray(activeCards) && activeCards.length > 0;

  const phaseLabel = isConnecting ? 'Connecting…'
    : muted ? 'Muted'
    : phase === 'speaking' ? 'Speaking'
    : phase === 'thinking' ? 'Thinking…'
    : 'Listening';

  const orbClasses = [
    'voice-orb-core',
    visualPhase === 'thinking' ? 'thinking' : '',
    visualPhase === 'speaking' ? 'speaking' : '',
    muted ? 'muted' : '',
  ].filter(Boolean).join(' ');

  // Build a top-bar timer string
  let timerText;
  if (state === 'connected') {
    if (remainingSeconds !== null && remainingSeconds > 0) {
      timerText = `${fmtTime(elapsed)} · ${fmtTime(remainingSeconds)} left`;
    } else {
      timerText = `${fmtTime(elapsed)} · live`;
    }
  } else {
    timerText = phaseLabel;
  }

  return (
    <div className="voice-overlay">
      <div className="voice-aurora">
        <div className="voice-aurora-blob-1" />
        <div className="voice-aurora-blob-2" />
        <div className="voice-aurora-blob-3" />
        <div className="voice-aurora-grain" />
        <div className="voice-aurora-vignette" />
      </div>

      <div className="voice-top-bar">
        <div className="voice-top-bar-left">
          <div className="voice-top-bar-logo">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
              <path d="M12 3l1.5 5.5L19 10l-5.5 1.5L12 17l-1.5-5.5L5 10l5.5-1.5L12 3Z" fill="#fff"/>
            </svg>
          </div>
          <div>
            <div className="voice-top-bar-title">
              f8 · voice
              {tier === 'premium' && (
                <span style={{
                  marginLeft: 8,
                  fontSize: 10,
                  fontWeight: 600,
                  padding: '2px 6px',
                  borderRadius: 999,
                  background: 'rgba(217,70,239,0.25)',
                }}>
                  premium
                </span>
              )}
            </div>
            <div className="voice-top-bar-timer">{timerText}</div>
          </div>
        </div>
        <button className="voice-close-btn" onClick={handleClose} title="Close (Esc)">
          <X size={16} />
        </button>
      </div>

      {/* Time warning pill (amber, non-blocking) */}
      {timeWarningSeconds && state === 'connected' && (
        <div style={{
          position: 'absolute',
          top: 64,
          left: '50%',
          transform: 'translateX(-50%)',
          padding: '6px 12px',
          borderRadius: 999,
          background: 'rgba(251, 191, 36, 0.18)',
          border: '1px solid rgba(251, 191, 36, 0.4)',
          color: '#fde68a',
          fontSize: 12,
          fontWeight: 600,
          zIndex: 50,
        }}>
          ~{timeWarningSeconds}s left in this session
        </div>
      )}

      <div className={`voice-center ${skillActive ? 'skill' : ''}`}>
        <div className="voice-left">
        <div className="voice-orb-stack">
          <div className={`voice-halo-outer ${visualPhase === 'speaking' ? 'speaking' : ''}`} />
          <div className={`voice-halo-mid ${visualPhase === 'speaking' ? 'speaking' : ''}`} />
          <div className={orbClasses} />

          {visualPhase === 'speaking' && (
            <div className="voice-waveform">
              {WAVEFORM_DELAYS.map((d, i) => (
                <div
                  key={i}
                  className="voice-waveform-bar"
                  style={{
                    animation: `voice-bar ${d}s ease-in-out infinite`,
                    animationDelay: `${i * 0.07}s`,
                  }}
                />
              ))}
            </div>
          )}

          {visualPhase === 'thinking' && (
            <div className="voice-thinking-dots">
              {[0, 1, 2].map(i => (
                <div
                  key={i}
                  className="voice-thinking-dot"
                  style={{ animationDelay: `${i * 0.15}s` }}
                />
              ))}
            </div>
          )}
        </div>

        <div className="voice-status-pill">
          <span className={`voice-status-dot ${muted ? 'muted' : visualPhase}`} />
          <span className="voice-status-label">{phaseLabel}</span>
        </div>

        <div className="voice-transcript">
          {quotaExhausted ? (
            <div style={{ textAlign: 'center', padding: '8px 0' }}>
              <div style={{
                fontSize: 14,
                fontWeight: 600,
                color: '#f0abfc',
                marginBottom: 8,
              }}>
                Free voice sessions used up
              </div>
              <div style={{
                fontSize: 12,
                color: 'rgba(220,200,240,0.7)',
                marginBottom: 14,
                maxWidth: 320,
                margin: '0 auto 14px',
              }}>
                {quotaReason || 'Upgrade to premium for monthly voice chat minutes.'}
              </div>
              <button
                onClick={handleUpgrade}
                style={{
                  appearance: 'none',
                  border: 0,
                  cursor: 'pointer',
                  padding: '8px 18px',
                  borderRadius: 999,
                  background: 'linear-gradient(135deg, #d946ef, #a855f7)',
                  color: '#fff',
                  fontSize: 12,
                  fontWeight: 700,
                  fontFamily: 'inherit',
                  boxShadow: '0 4px 16px rgba(217,70,239,0.32)',
                }}
              >
                Upgrade to premium
              </button>
            </div>
          ) : lastError ? (
            <div className="voice-transcript-muted" style={{ color: '#ff6b6b' }}>
              {lastError}
            </div>
          ) : muted ? (
            <div className="voice-transcript-muted">Tap unmute when you're ready to talk.</div>
          ) : phase === 'speaking' && agentTranscript ? (
            <>
              <div className="voice-transcript-label f8">F8</div>
              <div className="voice-transcript-text">{agentTranscript}</div>
            </>
          ) : phase === 'listening' && userTranscript ? (
            <>
              <div className="voice-transcript-label you">YOU</div>
              <div className="voice-transcript-text">
                {userTranscript}
                <span className="voice-transcript-cursor" />
              </div>
            </>
          ) : state === 'connected' ? (
            <div className="voice-transcript-muted">Say something to f8…</div>
          ) : null}
        </div>
        </div>

        {skillActive && (
          <div className="voice-skill-panel">
            <VoiceCardOverlay activeCards={activeCards} onDismiss={dismissCard} />
          </div>
        )}
      </div>

      <div className="voice-controls">
        <div className="voice-ctrl">
          <button
            className={`voice-ctrl-btn ${muted ? 'active' : ''}`}
            onClick={toggleMute}
            disabled={state !== 'connected'}
          >
            {muted ? (
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                <path d="M9 9v3a3 3 0 0 0 5.12 2.12M15 9.34V5a3 3 0 0 0-5.94-.6M5 5l14 14M19 10v2a7 7 0 0 1-.11 1.23M12 19v3M8 22h8" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              </svg>
            ) : (
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                <rect x="9" y="3" width="6" height="12" rx="3" stroke="currentColor" strokeWidth="2"/>
                <path d="M5 10v2a7 7 0 0 0 14 0v-2M12 19v3M8 22h8" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              </svg>
            )}
          </button>
          <span className="voice-ctrl-label">{muted ? 'Unmute' : 'Mute'}</span>
        </div>

        <button className="voice-end-btn" onClick={handleClose} title="End voice chat">
          <svg width="30" height="30" viewBox="0 0 24 24" fill="none">
            <path d="M21 15.5c-2.3-1.5-5.4-2.5-9-2.5s-6.7 1-9 2.5L1.5 13a14 14 0 0 1 21 0L21 15.5Zm-9-7.5c-3.3 0-6.4.9-9 2.4 0 0 5.5-7.4 9-7.4s9 7.4 9 7.4c-2.6-1.5-5.7-2.4-9-2.4Z" fill="currentColor"/>
            <path d="M3 12 21 12" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" transform="rotate(135 12 12)"/>
          </svg>
        </button>
      </div>

      <div className="voice-footnote">f8 voice mode</div>
    </div>
  );
}
